import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../services/api';
import ContactRequestCard from '../components/ContactRequestCard';
import SelecteurTelephone from '../components/SelecteurTelephone';
import ContactRequestDetailModal from '../components/ContactRequestDetailModal';

const ContactRequests = ({
  demandesContact: propsDemandesContact,
  demandesArchivees: propsDemandesArchivees,
  onChangeStatus,
  onAffecter,
  onDelete,
  onArchive,
  onUnarchive,
  onMarkAsProcessed
}) => {
  const [filter, setFilter] = useState('tous');
  const [selectedDemande, setSelectedDemande] = useState(null);
  // lot DJ — arrivée depuis une notification : /demandes-contact?demande={id}
  const [parametresUrl, setParametresUrl] = useSearchParams();
  const [demandeEnEvidence, setDemandeEnEvidence] = useState(null);
  const [showDemandeDetail, setShowDemandeDetail] = useState(false);
  
  // State local pour les données si non fournies via props
  const [localDemandesContact, setLocalDemandesContact] = useState([]);
  const [localDemandesArchivees, setLocalDemandesArchivees] = useState([]);
  const [loading, setLoading] = useState(false);

  // Charger les données si pas fournies via props
  useEffect(() => {
    if (!propsDemandesContact) {
      loadDemandesContact();
    }
  }, [propsDemandesContact]);

  const loadDemandesContact = async () => {
    try {
      setLoading(true);
      // Charger les demandes actives
      const response = await api.get('/contacts-agence');
      const demandes = response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setLocalDemandesContact(demandes);
      
      // Charger les demandes archivées
      const responseArchived = await api.get('/contacts-agence?include_archived=true');
      const archived = responseArchived.data
        .filter(d => d.archived === true)
        .sort((a, b) => new Date(b.archived_at || b.created_at) - new Date(a.archived_at || a.created_at));
      setLocalDemandesArchivees(archived);
    } catch (error) {
      console.error('Erreur chargement demandes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Utiliser les props si fournies, sinon les données locales
  const demandesContact = propsDemandesContact || localDemandesContact;

  // lot DJ — ?demande={id} : la demande est ouverte, mise en évidence 2 s, puis l'URL est nettoyée
  useEffect(() => {
    const cible = parametresUrl.get('demande');
    if (!cible || !demandesContact.length) return;
    const demande = demandesContact.find((d) => d.id === cible);
    if (demande) {
      setSelectedDemande(demande);
      setDemandeEnEvidence(cible);
      setTimeout(() => setDemandeEnEvidence(null), 2000);
    }
    const suivant = new URLSearchParams(parametresUrl);
    suivant.delete('demande');
    setParametresUrl(suivant, { replace: true });
  }, [parametresUrl, setParametresUrl, demandesContact]);
  const demandesArchivees = propsDemandesArchivees || localDemandesArchivees;

  const getFilteredDemandes = () => {
    // Si on filtre par "archivées", afficher les demandes archivées
    if (filter === 'archivées') {
      return demandesArchivees || [];
    }
    
    // Sinon, filtrer les demandes actives (non archivées)
    const demandes = demandesContact || [];
    return demandes.filter(d => {
      if (filter === 'tous') return true;
      if (filter === 'en_attente') return d.statut === 'en_attente' || d.statut === 'nouveau';
      return d.statut === filter;
    });
  };

  // Handlers locaux si pas fournis via props
  const handleChangeStatus = async (demandeId, statut) => {
    if (onChangeStatus) {
      onChangeStatus(demandeId, statut);
    } else {
      try {
        await api.put(`/contacts-agence/${demandeId}/statut`, { statut });
        loadDemandesContact();
      } catch (error) {
        console.error('Erreur changement statut:', error);
      }
    }
  };

  const handleArchive = async (demandeId) => {
    if (onArchive) {
      onArchive(demandeId);
    } else {
      try {
        await api.patch(`/contacts-agence/${demandeId}/archiver`);
        loadDemandesContact();
      } catch (error) {
        console.error('Erreur archivage:', error);
      }
    }
  };

  const handleUnarchive = async (demandeId) => {
    if (onUnarchive) {
      onUnarchive(demandeId);
    } else {
      try {
        await api.patch(`/contacts-agence/${demandeId}/desarchiver`);
        loadDemandesContact();
      } catch (error) {
        console.error('Erreur désarchivage:', error);
      }
    }
  };

  const handleDelete = async (demandeId) => {
    if (onDelete) {
      onDelete(demandeId);
    } else {
      if (!window.confirm('Supprimer cette demande ?')) return;
      try {
        await api.delete(`/contacts-agence/${demandeId}`);
        loadDemandesContact();
      } catch (error) {
        console.error('Erreur suppression:', error);
      }
    }
  };

  const handleAffecter = async (demande) => {
    if (onAffecter) {
      onAffecter(demande);
      return;
    }
    const bien = demande.property_reference ? ` et lui rattacher le bien ${demande.property_reference}` : '';
    if (!window.confirm(`Créer la fiche contact de ${demande.nom}${bien} ?`)) return;
    try {
      let r = await api.post(`/contacts-agence/${demande.id}/creer-contact`, {});
      // Le serveur est seul juge du doublon (même règle que le wizard) : il n'écrit rien
      // et nous laisse trancher entre rattacher et créer une fiche distincte.
      if (r.data?.action === 'doublon') {
        const c = r.data.contact || {};
        const nom = [c.prenom, c.nom].filter(Boolean).join(' ') || c.societe || 'Un contact';
        const rattacher = window.confirm(
          `${nom} porte déjà ces coordonnées.\n\nOK : lui rattacher la demande.\nAnnuler : créer une fiche distincte.`
        );
        r = await api.post(`/contacts-agence/${demande.id}/creer-contact`,
          rattacher ? { contact_id: c.id } : { forcer_nouveau: true });
      }
      toast.success(r.data?.message || 'Contact affecté');
      loadDemandesContact();
    } catch (error) {
      console.error('Erreur affectation:', error);
      toast.error(error.response?.data?.detail || "L'affectation a échoué");
    }
  };

  const handleViewDetails = (demande) => {
    setSelectedDemande(demande);
    setShowDemandeDetail(true);
  };

  const filteredDemandes = getFilteredDemandes();

  // lot DK2 — les cinq filtres, pour le menu deroulant du telephone
  const filtresDemandes = [
    { cle: 'tous', libelle: 'Tous', compte: demandesContact.length },
    { cle: 'en_attente', libelle: 'En attente', compte: demandesContact.filter(d => d.statut === 'en_attente' || d.statut === 'nouveau').length },
    { cle: 'en_cours', libelle: 'En cours', compte: demandesContact.filter(d => d.statut === 'en_cours').length },
    { cle: 'traité', libelle: 'Traitées', compte: demandesContact.filter(d => d.statut === 'traité').length },
    { cle: 'archivées', libelle: 'Archivées', compte: demandesArchivees.length },
  ];

  return (
    <>
      <div className="p-4 md:p-8 bg-slate-50" data-testid="contact-requests-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <i className="bi bi-envelope-fill text-white text-xl"></i>
            </div>
            <h1 className="text-3xl font-bold text-slate-800">Demandes de contact</h1>
          </div>
          <p className="text-slate-600 text-sm ml-13">Gérez vos demandes de renseignements et prospects</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm">
        
        {/* lot DK2 — telephone : un menu deroulant ; ordinateur : les onglets d'origine */}
        <div className="px-4 md:px-6 py-4 border-b border-gray-100">
          <div className="xl:hidden">
            <SelecteurTelephone
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              options={filtresDemandes.map((f) => ({ valeur: f.cle, libelle: `${f.libelle} (${f.compte})` }))}
              ariaLabel="Filtrer les demandes"
              testId="demandes-filtre-select"
            />
          </div>
          <div className="hidden xl:flex bg-white rounded-xl shadow-sm border border-slate-200 p-2 gap-2 flex-wrap"
               data-testid="demandes-filtres">
            <button
              onClick={() => setFilter('tous')}
              className={`flex items-center gap-2 px-4 h-10 md:h-auto md:py-2.5 shrink-0 whitespace-nowrap rounded-lg font-semibold text-sm transition-all ${
                filter === 'tous'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <span>Tous</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                filter === 'tous' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-100 text-slate-700'
              }`}>
                {demandesContact.length}
              </span>
            </button>
            <button
              onClick={() => setFilter('en_attente')}
              className={`flex items-center gap-2 px-4 h-10 md:h-auto md:py-2.5 shrink-0 whitespace-nowrap rounded-lg font-semibold text-sm transition-all ${
                filter === 'en_attente'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <i className="bi bi-hourglass-split text-orange-500"></i>
              <span>En attente</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                filter === 'en_attente' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-100 text-slate-700'
              }`}>
                {demandesContact.filter(d => d.statut === 'en_attente' || d.statut === 'nouveau').length}
              </span>
            </button>
            <button
              onClick={() => setFilter('en_cours')}
              className={`flex items-center gap-2 px-4 h-10 md:h-auto md:py-2.5 shrink-0 whitespace-nowrap rounded-lg font-semibold text-sm transition-all ${
                filter === 'en_cours'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <i className="bi bi-arrow-repeat text-blue-500"></i>
              <span>En cours</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                filter === 'en_cours' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-100 text-slate-700'
              }`}>
                {demandesContact.filter(d => d.statut === 'en_cours').length}
              </span>
            </button>
            <button
              onClick={() => setFilter('traité')}
              className={`flex items-center gap-2 px-4 h-10 md:h-auto md:py-2.5 shrink-0 whitespace-nowrap rounded-lg font-semibold text-sm transition-all ${
                filter === 'traité'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <i className="bi bi-check-circle text-green-500"></i>
              <span>Traitées</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                filter === 'traité' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-100 text-slate-700'
              }`}>
                {demandesContact.filter(d => d.statut === 'traité').length}
              </span>
            </button>
            <button
              onClick={() => setFilter('archivées')}
              data-testid="demandes-filter-archivees"
              className={`flex items-center gap-2 px-4 h-10 md:h-auto md:py-2.5 shrink-0 whitespace-nowrap rounded-lg font-semibold text-sm transition-all ${
                filter === 'archivées'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <i className="bi bi-archive text-amber-600"></i>
              <span>Archivées</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                filter === 'archivées' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-100 text-slate-700'
              }`}>
                {demandesArchivees.length}
              </span>
            </button>
          </div>
        </div>
        
        {/* Liste des demandes */}
        <div className="p-4 md:p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
              <p className="text-gray-500">Chargement des demandes...</p>
            </div>
          ) : filteredDemandes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <i className="bi bi-inbox text-4xl mb-3 block"></i>
              <p>Aucune demande de contact</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* En-tête des colonnes — ordinateur seulement (lot DK3 : à partir de 1 280 px) */}
              <div className="hidden xl:flex items-center gap-4 px-4 pb-1" data-testid="demandes-entetes-colonnes">
                <div className="w-12 flex-shrink-0" aria-hidden="true"></div>
                <div className="flex-1 min-w-0 grid grid-cols-12 gap-3 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  <div className="col-span-3">Contact</div>
                  <div className="col-span-2">Message</div>
                  <div className="col-span-2">Coordonnées</div>
                  <div className="col-span-3">Bien concerné</div>
                  <div className="col-span-2 text-right">Réf. du bien</div>
                </div>
                <div className="w-[104px] flex-shrink-0 text-[11px] font-bold uppercase tracking-wide text-slate-500">Statut</div>
                <div className="w-[116px] flex-shrink-0 text-[11px] font-bold uppercase tracking-wide text-slate-500 text-right">Actions</div>
              </div>
              {filteredDemandes.map(demande => (
                <div key={demande.id}
                     data-testid={`demande-ligne-${demande.id}`}
                     className={demandeEnEvidence === demande.id
                       ? 'ring-2 ring-blue-400 rounded-lg transition' : ''}>
                <ContactRequestCard
                  demande={demande}
                  onChangeStatus={handleChangeStatus}
                  onAffecter={handleAffecter}
                  onDelete={handleDelete}
                  onArchive={handleArchive}
                  onUnarchive={handleUnarchive}
                  onViewDetails={handleViewDetails}
                  isArchived={filter === 'archivées'}
                />
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Modal détail demande */}
      <ContactRequestDetailModal
        demande={selectedDemande}
        isOpen={showDemandeDetail}
        onClose={() => setShowDemandeDetail(false)}
        onMarkAsProcessed={onMarkAsProcessed}
      />
    </>
  );
};

export default ContactRequests;
